/**
 * DealPreviewPane — CENTER pane (min-width 480px) in the Inbox Cockpit.
 * Renders the selected deal's preview: company header, verdict chip,
 * Deal Screen sections (Problem / Solution / Market Signal), and call decision.
 *
 * Authority: WI-UI-CLEANUP-001 Stage 2, SA-4.
 * Design tokens: DS-CARD-V5 from ui-invariants.js — zero inline hex.
 *
 * Props:
 *   - selected: InboxItem | null
 */

import React, { useState } from 'react';
import { CircleNotch } from '@phosphor-icons/react';
import {
  CALL_BADGE,
  DECISION_RATIONALE,
  DS_COLORS,
  DS_TYPOGRAPHY,
} from '@/config/ui-invariants';
import { COCKPIT_LABELS, INTENT_TO_BUTTON, WORK_TYPES } from '@/config/task-display-config';
import { Button } from '@/components/ui/button';
import { deriveVerdictPhrase } from '@/lib/verdictPhrase';
import { ScrollArea } from '@/components/ui/scroll-area';
import useAnnouncer from '@/hooks/useAnnouncer';
import VerdictChip from './VerdictChip';
import SectionPanel from '@/components/deal-screen/SectionPanel';

// DECISION: deal-screen work types that should populate this pane.
// Tracxn live deals and deal-screen work items both carry a deal_screen payload.
// deal_screen_review and pitch_deck_review are speculative names not in
// WORK_TYPES today — left as raw literals (suppressed) until they're added
// to the canonical authority. // noscan
const DEAL_SCREEN_WORK_TYPES = new Set([
  WORK_TYPES.TRACXN_LIVEDEALS,
  WORK_TYPES.FIRST_LOOK,
  WORK_TYPES.DEAL_SCREEN,
  'deal_screen_review', // noscan
  'pitch_deck_review',  // noscan
]);

const SECTION_ORDER = ['problem', 'solution', 'market_signal'];

// ── Helpers ──────────────────────────────────────────────────────────────────

function resolveCallBadgeTokens(callDecision) {
  if (callDecision === 'worth_a_call') return CALL_BADGE.worthACall;
  return CALL_BADGE.notWorthACall;
}

function resolveCallLabel(callDecision) {
  if (callDecision === 'worth_a_call') return 'Worth a Call';
  if (callDecision === 'not_worth_a_call') return 'Not Worth a Call';
  return callDecision || '';
}

// ── Sub-components ────────────────────────────────────────────────────────────

function CallDecisionBadge({ callDecision }) {
  const tokens = resolveCallBadgeTokens(callDecision);
  const shape = CALL_BADGE.badge;

  return (
    <span
      data-testid="call-decision-badge"
      style={{
        background: tokens.background,
        color: tokens.text,
        border: `${shape.borderWidth} solid ${tokens.border}`,
        borderRadius: shape.borderRadius,
        fontSize: shape.fontSize,
        fontWeight: shape.fontWeight,
        padding: `${shape.paddingTop} ${shape.paddingRight} ${shape.paddingBottom} ${shape.paddingLeft}`,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        whiteSpace: 'nowrap',
      }}
    >
      {tokens.dotAnimation === 'pulse' && (
        <span
          style={{
            width: tokens.dotSize,
            height: tokens.dotSize,
            borderRadius: '50%',
            background: tokens.dot,
            display: 'inline-block',
            animation: 'pulse 2s infinite',
          }}
        />
      )}
      {resolveCallLabel(callDecision)}
    </span>
  );
}

function DecisionRationaleStrip({ reason }) {
  return (
    <div
      data-testid="decision-rationale-strip"
      style={{
        background: DECISION_RATIONALE.shell.background,
        borderLeft: DECISION_RATIONALE.shell.borderLeft,
        border: DECISION_RATIONALE.shell.borderOther,
        borderRadius: DECISION_RATIONALE.shell.borderRadius,
        padding: DECISION_RATIONALE.shell.padding,
        marginTop: '16px',
      }}
    >
      <p
        style={{
          ...DS_TYPOGRAPHY.bodySm,
          color: DS_COLORS.text.body,
          margin: 0,
        }}
      >
        {reason}
      </p>
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div
      data-testid="deal-preview-empty"
      style={{
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        height: '100%',
      }}
    >
      <p
        style={{
          color: DS_COLORS.text.muted,
          fontSize: '16px',
          fontFamily: 'Inter, system-ui, sans-serif',
          margin: 0,
          textAlign: 'center',
        }}
      >
        Select a deal from the list
      </p>
    </div>
  );
}

// ── Action bar (WI-INBOX-BUTTONS-001) ────────────────────────────────────────
//
// Pass (negative)  — onReject(id, 'pass', work_type) → /inbox/{id}/reject
// Send to FCA      — currently DISABLED. fca.py/fca_read.py do not expose an
//                    initial trigger endpoint (only /rerun/{trigger}). Per
//                    WI-INBOX-BUTTONS-001: never render a ghost button —
//                    show disabled "coming soon" until the trigger contract
//                    is confirmed in a follow-up WI.
// Schedule Call    — onAccept(id, work_type, {}) → /inbox/{id}/accept

function DealActionBar({ item, onAccept, onReject }) {
  const [busyAction, setBusyAction] = useState(null); // 'pass' | 'schedule' | null

  // WI-GATEENVELOPE-SILENT-200-AUDIT-AND-FIX-001 (L-055): these are investment
  // decisions, and the accept/reject endpoints can now return a real non-200.
  // Before this, the only failure signal was a toast from the cockpit hook —
  // not reliably announced (#950), so for the CEO the button simply stopped
  // spinning and nothing was said. Announce the failure.
  const [actionStatus, announceAction] = useAnnouncer();

  const handlePass = async () => {
    if (!onReject || busyAction) return;
    setBusyAction('pass');
    try {
      await onReject(item.id, 'pass', item.work_type);
    } catch (err) {
      announceAction(err?.message || 'Could not pass on this deal — please try again.');
    } finally {
      setBusyAction(null);
    }
  };

  const handleScheduleCall = async () => {
    if (!onAccept || busyAction) return;
    setBusyAction('schedule');
    try {
      await onAccept(item.id, item.work_type, {});
    } catch (err) {
      announceAction(err?.message || 'Could not schedule this call — please try again.');
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div
      data-testid="deal-preview-action-bar"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px 0',
        borderTop: `1px solid ${DS_COLORS.border.default}`,
        borderBottom: `1px solid ${DS_COLORS.border.default}`,
        marginBottom: '24px',
      }}
    >
      <div role="status" aria-live="polite" className="sr-only">{actionStatus}</div>
      <button
        type="button"
        data-testid="deal-pass-btn"
        onClick={handlePass}
        disabled={!!busyAction || !onReject}
        className={`min-h-[44px] px-5 py-2.5 rounded-md font-semibold text-sm transition-colors border ${INTENT_TO_BUTTON.negative.bg} ${INTENT_TO_BUTTON.negative.text} ${INTENT_TO_BUTTON.negative.border} ${INTENT_TO_BUTTON.negative.hover} disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2`}
        style={{ fontFamily: 'DM Sans, system-ui, sans-serif' }}
      >
        {busyAction === 'pass' && (
          <CircleNotch weight="bold" className="w-4 h-4 animate-spin" />
        )}
        {busyAction === 'pass' ? 'Passing...' : 'Pass'}
      </button>

      <button
        type="button"
        data-testid="deal-fca-btn"
        disabled
        className={`min-h-[44px] px-5 py-2.5 rounded-md font-semibold text-sm border ${INTENT_TO_BUTTON.muted.bg} ${INTENT_TO_BUTTON.muted.text} ${INTENT_TO_BUTTON.muted.border} cursor-not-allowed inline-flex items-center gap-2`}
        style={{ fontFamily: 'DM Sans, system-ui, sans-serif' }}
      >
        {COCKPIT_LABELS.inspectorActions.sendToFCA} — {COCKPIT_LABELS.inspectorActions.comingNextWi}
      </button>

      <div style={{ flex: 1 }} />

      <button
        type="button"
        data-testid="deal-schedule-call-btn"
        onClick={handleScheduleCall}
        disabled={!!busyAction || !onAccept}
        className={`min-h-[44px] px-5 py-2.5 rounded-md font-semibold text-sm transition-colors ${INTENT_TO_BUTTON.primary.bg} ${INTENT_TO_BUTTON.primary.text} ${INTENT_TO_BUTTON.primary.hover} disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2`}
        style={{ fontFamily: 'DM Sans, system-ui, sans-serif' }}
      >
        {busyAction === 'schedule' && (
          <CircleNotch weight="bold" className="w-4 h-4 animate-spin" />
        )}
        {busyAction === 'schedule' ? 'Scheduling...' : 'Schedule Call'}
      </button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function DealPreviewPane({
  selected,
  onOpenInspector,
  onAccept,
  onReject,
}) {
  const isEmpty =
    !selected || !DEAL_SCREEN_WORK_TYPES.has(selected.work_type);

  return (
    <div
      data-testid="deal-preview-pane"
      data-selected-id={selected?.id || 'none'}
      role="region"
      aria-label={COCKPIT_LABELS.aria.preview}
      style={{
        flex: 1,
        minWidth: '480px',
        display: 'flex',
        flexDirection: 'column',
        borderRight: `1px solid ${DS_COLORS.border.default}`,
        background: DS_COLORS.surface.white,
        overflow: 'hidden',
      }}
    >
      {isEmpty ? (
        <EmptyState />
      ) : (
        <ScrollArea style={{ flex: 1, height: '100%' }}>
          <div style={{ padding: '24px' }}>
            {/* Header block */}
            <div
              style={{
                paddingBottom: '24px',
                borderBottom: `1px solid ${DS_COLORS.border.default}`,
                marginBottom: '24px',
              }}
            >
              {/* Company name */}
              <h1
                data-testid="company-name"
                style={{
                  fontFamily: 'DM Sans, system-ui, sans-serif',
                  fontSize: '24px',
                  fontWeight: 800,
                  color: DS_COLORS.text.primary,
                  margin: '0 0 10px',
                  lineHeight: 1.2,
                }}
              >
                {selected.title}
              </h1>

              {/* Meta row: sector chip + verdict chip */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '8px',
                  marginBottom: selected.payload?.deal_screen?.call_decision
                    ? '14px'
                    : '0',
                }}
              >
                {/* Law §8.4: the chip says the sector only in the four state words (backend `sector_state`), never the screen's own sector text. */}
                <span
                  data-testid="sector-chip"
                  style={{
                    display: 'inline-block',
                    background: DS_COLORS.surface.slate,
                    color: DS_COLORS.text.secondary,
                    border: `1px solid ${DS_COLORS.border.default}`,
                    borderRadius: '9999px',
                    fontSize: '12px',
                    fontWeight: 600,
                    padding: '3px 10px',
                    fontFamily: 'DM Sans, system-ui, sans-serif',
                  }}
                >
                  {selected.payload?.sector_state?.compact_label || COCKPIT_LABELS.taxonomy.sectorNotChecked}
                </span>

                {/* Verdict chip from VerdictChip (SA-3) */}
                {(() => {
                  const { phrase, tone } = deriveVerdictPhrase(selected);
                  return <VerdictChip phrase={phrase} tone={tone} />;
                })()}
              </div>

              {/* Call decision badge */}
              {selected.payload?.deal_screen?.call_decision && (
                <CallDecisionBadge
                  callDecision={selected.payload.deal_screen.call_decision}
                />
              )}

              {/* Drawer trigger — visible only when onOpenInspector prop is passed (tablet/narrow-tablet/mobile) */}
              {onOpenInspector && (
                <Button
                  variant="outline"
                  onClick={onOpenInspector}
                  data-testid="open-inspector-btn"
                  style={{ minHeight: 44, marginTop: 12 }}
                >
                  {COCKPIT_LABELS.inspectorActions.openInspector}
                </Button>
              )}
            </div>

            {/* Action bar — Pass / Send to FCA (disabled) / Schedule Call.
                WI-INBOX-BUTTONS-001. Placed above the analysis sections so
                CEO can act without scrolling past every panel first. */}
            <DealActionBar
              item={selected}
              onAccept={onAccept}
              onReject={onReject}
            />

            {/* Deal Screen sections */}
            {selected.payload?.deal_screen && (
              <div data-testid="deal-screen-sections">
                {SECTION_ORDER.map((sectionKey) => {
                  const section =
                    selected.payload.deal_screen[sectionKey];
                  if (!section) return null;
                  return (
                    <SectionPanel
                      key={sectionKey}
                      section={section}
                      sectionKey={sectionKey}
                      onRerun={undefined}
                      rerunning={false}
                    />
                  );
                })}
              </div>
            )}

            {/* Decision rationale strip */}
            {selected.payload?.deal_screen?.call_decision_reason && (
              <DecisionRationaleStrip
                reason={selected.payload.deal_screen.call_decision_reason}
              />
            )}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
